// Drawer admin para crear/editar Grammar Bank: tabs Datos + Activities.

import { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { ApiError } from '@/services/api';
import { listLevels, type CatalogLevel } from '@/services/catalogs';
import {
  adminCreateGrammarBank,
  adminDeleteGrammarBank,
  adminReplaceActivities,
  adminUpdateGrammarBank,
  getCatalogs,
  type ActivityInput,
  type GrammarBankActivity,
  type GrammarBankListItem,
  type GrammarCategory,
  type GrammarStatement,
} from '@/services/grammar-banks';
import { adminListQuizzes, type QuizMeta } from '@/services/quizzes';
import styles from './GrammarBankEditorDrawer.module.scss';

interface CreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create';
  onSaved: () => void;
}
interface EditProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'edit';
  item: GrammarBankListItem;
  activities: GrammarBankActivity[];
  onSaved: () => void;
}
type Props = CreateProps | EditProps;

type TabKey = 'datos' | 'activities';

interface DraftActivity {
  position: number;
  statement_id: number | null;
  content: string;
  quiz_id: number | null;
}

const EMPTY_ACTIVITY: Omit<DraftActivity, 'position'> = {
  statement_id: null,
  content: '',
  quiz_id: null,
};

export function GrammarBankEditorDrawer(props: Props) {
  const { open, onOpenChange, mode, onSaved } = props;
  const isEdit = mode === 'edit';

  const [tab, setTab] = useState<TabKey>('datos');
  const [levels, setLevels] = useState<CatalogLevel[]>([]);
  const [categories, setCategories] = useState<GrammarCategory[]>([]);
  const [statements, setStatements] = useState<GrammarStatement[]>([]);
  const [quizzes, setQuizzes] = useState<QuizMeta[]>([]);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [levelId, setLevelId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [isPublished, setIsPublished] = useState(true);

  const [activities, setActivities] = useState<DraftActivity[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTab('datos');
    Promise.all([listLevels(), getCatalogs(), adminListQuizzes()])
      .then(([lvls, cat, quizMeta]) => {
        setLevels(lvls);
        setCategories(cat.categories);
        setStatements(cat.statements);
        setQuizzes(quizMeta);
      })
      .catch(() => {
        /* silenciar — los selectores se quedan vacíos */
      });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const it = (props as EditProps).item;
      const acts = (props as EditProps).activities;
      setTitle(it.title);
      setSlug(it.slug);
      setContent(it.content ?? '');
      setCoverImageUrl(it.cover_image_url ?? '');
      setLevelId(it.level_id);
      setCategoryId(it.category_id);
      setIsPublished(it.is_published === 1);
      setActivities(
        acts.map((a) => ({
          position: a.position,
          statement_id: a.statement_id,
          content: a.content ?? '',
          quiz_id: a.quiz_id,
        }))
      );
    } else {
      setTitle('');
      setSlug('');
      setContent('');
      setCoverImageUrl('');
      setLevelId(null);
      setCategoryId(null);
      setIsPublished(true);
      setActivities([]);
    }
  }, [open, isEdit, props]);

  const slugify = useCallback((raw: string): string => {
    return raw
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 200);
  }, []);

  const onTitleChange = (v: string) => {
    setTitle(v);
    if (!isEdit && !slug) setSlug(slugify(v));
  };

  function addActivity() {
    setActivities((prev) => [
      ...prev,
      { ...EMPTY_ACTIVITY, position: prev.length > 0 ? Math.max(...prev.map((a) => a.position)) + 1 : 1 },
    ]);
  }

  function removeActivity(position: number) {
    setActivities((prev) => prev.filter((a) => a.position !== position).map((a, i) => ({ ...a, position: i + 1 })));
  }

  function updateActivity(position: number, patch: Partial<DraftActivity>) {
    setActivities((prev) => prev.map((a) => (a.position === position ? { ...a, ...patch } : a)));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      let id: number;
      if (isEdit) {
        id = (props as EditProps).item.id;
        await adminUpdateGrammarBank(id, {
          title,
          slug,
          content: content || null,
          cover_image_url: coverImageUrl || null,
          level_id: levelId,
          category_id: categoryId,
          is_published: isPublished,
        });
      } else {
        const created = await adminCreateGrammarBank({
          title,
          slug,
          content: content || null,
          cover_image_url: coverImageUrl || null,
          level_id: levelId,
          category_id: categoryId,
          is_published: isPublished,
        });
        id = created.id;
      }
      const acts: ActivityInput[] = activities.map((a) => ({
        position: a.position,
        statement_id: a.statement_id,
        content: a.content || null,
        quiz_id: a.quiz_id,
      }));
      await adminReplaceActivities(id, acts);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    if (!confirm('¿Borrar esta ficha? Quedará archivada y dejará de aparecer en el listado.')) return;
    setBusy(true);
    setError(null);
    try {
      await adminDeleteGrammarBank((props as EditProps).item.id);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.titleHeader}>
              {isEdit ? 'Editar ficha' : 'Nueva ficha'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={16} />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'datos'}
              className={`${styles.tab} ${tab === 'datos' ? styles.tabActive : ''}`}
              onClick={() => setTab('datos')}
            >
              Datos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'activities'}
              className={`${styles.tab} ${tab === 'activities' ? styles.tabActive : ''}`}
              onClick={() => setTab('activities')}
            >
              Activities ({activities.length})
            </button>
          </div>

          <div className={styles.body}>
            {tab === 'datos' ? (
              <>
                <label className={styles.field}>
                  <span>Título</span>
                  <input value={title} onChange={(e) => onTitleChange(e.target.value)} maxLength={255} />
                </label>
                <label className={styles.field}>
                  <span>Slug</span>
                  <input
                    value={slug}
                    onChange={(e) => setSlug(slugify(e.target.value))}
                    maxLength={200}
                    placeholder="ej. past-simple"
                  />
                </label>
                <div className={styles.fieldRow}>
                  <label className={styles.field}>
                    <span>Nivel</span>
                    <select value={levelId ?? ''} onChange={(e) => setLevelId(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">—</option>
                      {levels.map((l) => (
                        <option key={l.id} value={l.id}>{l.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.field}>
                    <span>Categoría</span>
                    <select value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">—</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className={styles.field}>
                  <span>Imagen de portada (URL)</span>
                  <input
                    type="url"
                    value={coverImageUrl}
                    onChange={(e) => setCoverImageUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </label>
                <label className={styles.field}>
                  <span>Contenido (HTML)</span>
                  <textarea
                    className={styles.contentArea}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Pega aquí el HTML de la teoría (Gutenberg blocks aceptados)"
                  />
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                  />
                  <span>Publicada (visible para alumnos)</span>
                </label>
              </>
            ) : (
              <>
                {activities.length === 0 ? (
                  <p className={styles.muted}>Sin actividades. Añade la primera abajo.</p>
                ) : (
                  <ul className={styles.activitiesList}>
                    {activities.map((a) => (
                      <li key={a.position} className={styles.activityRow}>
                        <div className={styles.activityHeader}>
                          <strong>#{String(a.position).padStart(2, '0')}</strong>
                          <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() => removeActivity(a.position)}
                            aria-label="Eliminar actividad"
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                        <label className={styles.field}>
                          <span>Statement</span>
                          <select
                            value={a.statement_id ?? ''}
                            onChange={(e) =>
                              updateActivity(a.position, {
                                statement_id: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">—</option>
                            {statements.map((s) => (
                              <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className={styles.field}>
                          <span>Descripción / enunciado libre</span>
                          <input
                            value={a.content}
                            onChange={(e) => updateActivity(a.position, { content: e.target.value })}
                            maxLength={5000}
                          />
                        </label>
                        <label className={styles.field}>
                          <span>Quiz vinculado</span>
                          <select
                            value={a.quiz_id ?? ''}
                            onChange={(e) =>
                              updateActivity(a.position, {
                                quiz_id: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">Sin quiz</option>
                            {quizzes.map((q) => (
                              <option key={q.id} value={q.id}>
                                #{q.id} — {q.title}
                              </option>
                            ))}
                          </select>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <Button variant="secondary" onClick={addActivity}>
                  <Icon name="plus" size={14} /> Añadir actividad
                </Button>
              </>
            )}
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <footer className={styles.footer}>
            {isEdit && (
              <Button variant="secondary" onClick={handleDelete} disabled={busy}>
                <Icon name="trash" size={14} /> Eliminar
              </Button>
            )}
            <div className={styles.footerRight}>
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={save} disabled={busy || !title || !slug}>
                {busy ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
